import { useState, useEffect, useRef, useCallback } from "react";
import {
  Loader2,
  Gift,
  Star,
  Zap,
  Mail,
  Crown,
  GraduationCap,
  Package,
  Truck,
  ShoppingCart,
  X,
  Check,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import confetti from "canvas-confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function generateFingerprint() {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.textBaseline = "top";
  ctx.font = "14px Arial";
  ctx.fillText("fp", 2, 2);
  const canvasData = canvas.toDataURL();
  
  const raw = [
    navigator.userAgent,
    navigator.language,
    screen.width + "x" + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || "",
    navigator.platform || "",
    canvasData.slice(-50),
  ].join("|");
  
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) - hash + raw.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

const DEVICE_FP = generateFingerprint();
const fpHeaders = { "X-Device-Fingerprint": DEVICE_FP };

const PRIZE_ICONS = {
  formation: GraduationCap,
  "10_products": Package,
  "10_suppliers": Truck,
  "1month_app": Crown,
  "1month_academy": Star,
};

const SEGMENTS = [
  { id: "10_products", label: "10 Produits", color: "#00E676", textColor: "#0A0A0A" },
  { id: "1month_academy", label: "1 Mois Académie", color: "#9C27B0", textColor: "#FFFFFF" },
  { id: "10_suppliers", label: "10 Fournisseurs", color: "#00BCD4", textColor: "#0A0A0A" },
  { id: "formation", label: "Formation", color: "#FFD700", textColor: "#0A0A0A" },
  { id: "10_products", label: "10 Produits", color: "#00E676", textColor: "#0A0A0A" },
  { id: "1month_app", label: "1 Mois App", color: "#FF5722", textColor: "#FFFFFF" },
  { id: "10_suppliers", label: "10 Fournisseurs", color: "#00BCD4", textColor: "#0A0A0A" },
  { id: "10_products", label: "10 Produits", color: "#00E676", textColor: "#0A0A0A" },
];

function WheelCanvas({ spinning, targetIndex, onSpinEnd }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const rotRef = useRef(0);

  useEffect(() => {
    drawWheel(rotRef.current);
  }, []);

  useEffect(() => {
    if (!spinning || targetIndex === null) return;
    const segAngle = 360 / SEGMENTS.length;
    const targetAngle = 360 * 6 + (360 - targetIndex * segAngle - segAngle / 2);
    const startRot = rotRef.current % 360;
    const totalSpin = targetAngle - startRot;
    const duration = 4000;
    const startTime = performance.now();

    function animate(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const currentRot = startRot + totalSpin * eased;
      rotRef.current = currentRot;
      drawWheel(currentRot);
      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        onSpinEnd?.();
      }
    }
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [spinning, targetIndex]);

  function drawWheel(rotation) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const size = canvas.width;
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 8;
    const segAngle = (2 * Math.PI) / SEGMENTS.length;

    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((rotation * Math.PI) / 180);

    SEGMENTS.forEach((seg, i) => {
      const startA = i * segAngle - Math.PI / 2;
      const endA = startA + segAngle;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, r, startA, endA);
      ctx.closePath();
      ctx.fillStyle = seg.color;
      ctx.fill();
      ctx.strokeStyle = "#0A0A0A";
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.save();
      ctx.rotate(startA + segAngle / 2);
      ctx.textAlign = "right";
      ctx.fillStyle = seg.textColor;
      ctx.font = "bold 11px sans-serif";
      ctx.fillText(seg.label, r - 16, 4);
      ctx.restore();
    });

    // Center circle
    ctx.beginPath();
    ctx.arc(0, 0, 28, 0, Math.PI * 2);
    ctx.fillStyle = "#0A0A0A";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(0, 0, 22, 0, Math.PI * 2);
    ctx.fillStyle = "#FFD700";
    ctx.fill();
    ctx.fillStyle = "#0A0A0A";
    ctx.font = "bold 11px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("SPIN", 1, 5);

    ctx.restore();

    // Arrow pointer (top)
    ctx.beginPath();
    ctx.moveTo(cx, 4);
    ctx.lineTo(cx - 14, 28);
    ctx.lineTo(cx + 14, 28);
    ctx.closePath();
    ctx.fillStyle = "#FF5722";
    ctx.fill();
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  return <canvas ref={canvasRef} width={320} height={320} className="mx-auto" />;
}

export default function SpinWheel() {
  const { user } = useAuth();
  const email = user?.email || "";
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [targetIndex, setTargetIndex] = useState(null);
  const [prize, setPrize] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buyingPack, setBuyingPack] = useState(null);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [myPrizes, setMyPrizes] = useState([]);
  const [expandedPrize, setExpandedPrize] = useState(null);

  useEffect(() => {
    if (email) {
      fetchStatus();
      fetchMyPrizes();
      // Check for spin purchase completion after Stripe redirect
      const params = new URLSearchParams(window.location.search);
      if (params.get("purchased") === "true") {
        checkSpinPurchase();
      }
    } else {
      setLoading(false);
    }
  }, [email]);

  const checkSpinPurchase = async () => {
    // Try to find the most recent pending spin transaction for this user
    try {
      const txRes = await axios.get(`${API}/spin/status?email=${encodeURIComponent(email)}`, { headers: fpHeaders });
      // Poll payment_transactions for latest spin purchase
      const allTx = await axios.get(`${API}/user/recent-spin-purchase?email=${encodeURIComponent(email)}`);
      if (allTx.data.session_id) {
        // Poll Stripe status
        let attempts = 0;
        const poll = async () => {
          try {
            const res = await axios.get(`${API}/spin/purchase/status/${allTx.data.session_id}`);
            if (res.data.status === "paid") {
              toast.success(`${res.data.spins_credited} spins credites sur votre compte !`);
              fetchStatus();
              // Remove ?purchased from URL
              window.history.replaceState({}, "", window.location.pathname);
              return;
            }
          } catch {}
          attempts++;
          if (attempts < 5) setTimeout(poll, 2000);
        };
        poll();
      }
    } catch {}
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/spin/status?email=${encodeURIComponent(email)}`, { headers: fpHeaders });
      setStatus(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPrizes = async () => {
    if (!email) return;
    try {
      const res = await axios.get(`${API}/spin/my-prizes?email=${encodeURIComponent(email)}`);
      setMyPrizes(res.data.prizes || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSpin = async () => {
    if (spinning) return;
    if (!email) {
      toast.error("Connectez-vous pour jouer");
      return;
    }
    if (status && status.total_available <= 0) {
      toast.error("Plus de spins ! Achetez des spins supplementaires.");
      return;
    }
    // First call the API to get result (DON'T start animation yet)
    setPrize(null);
    setShowResult(false);
    try {
      const res = await axios.post(`${API}/spin/play?email=${encodeURIComponent(email)}`, {}, { headers: fpHeaders });
      const wonPrize = res.data.prize;
      const segIdx = SEGMENTS.findIndex((s) => s.id === wonPrize.id);
      // Set target BEFORE spinning so animation uses correct target
      setTargetIndex(segIdx >= 0 ? segIdx : 0);
      setPrize({ ...wonPrize, delivery: res.data.delivery });
      setStatus((prev) => ({
        ...prev,
        free_remaining: res.data.free_remaining,
        paid_remaining: res.data.paid_remaining,
        total_available: res.data.free_remaining + res.data.paid_remaining,
      }));
      // NOW start the animation after a tick so React processes targetIndex first
      setTimeout(() => setSpinning(true), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du spin");
    }
  };

  const fireCelebration = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ["#00E676", "#FFD700", "#FF5722", "#9C27B0", "#00BCD4", "#FFCC00"];
    
    // Initial big burst
    confetti({ particleCount: 100, spread: 90, origin: { y: 0.6 }, colors });
    
    // Continuous fireworks effect
    const interval = setInterval(() => {
      if (Date.now() > end) return clearInterval(interval);
      confetti({
        particleCount: 30,
        angle: 60 + Math.random() * 60,
        spread: 60,
        origin: { x: Math.random(), y: Math.random() * 0.4 },
        colors,
        startVelocity: 30 + Math.random() * 20,
      });
    }, 200);
  }, []);

  const onSpinEnd = () => {
    setSpinning(false);
    setShowResult(true);
    fireCelebration();
  };

  const handleBuyPack = async (packId) => {
    if (!email) {
      toast.error("Connectez-vous pour acheter");
      return;
    }
    setBuyingPack(packId);
    try {
      const res = await axios.post(`${API}/spin/purchase`, {
        email,
        pack: packId,
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error("Erreur lors de l'achat");
    } finally {
      setBuyingPack(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#FFD700]" size={48} />
      </div>
    );
  }

  const PrizeIcon = prize ? PRIZE_ICONS[prize.id] || Gift : Gift;

  return (
    <div className="space-y-6" data-testid="spin-wheel-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center justify-center gap-3">
          <Gift className="text-[#FFD700]" size={36} />
          ROUE DE LA FORTUNE
        </h1>
        <p className="text-muted-foreground mt-1 uppercase tracking-wider text-xs sm:text-sm">
          Tournez la roue et gagnez des lots exclusifs
        </p>
      </div>

      {/* Spins counter */}
      {status && (
        <div className="flex justify-center gap-4 flex-wrap" data-testid="spin-counters">
          <div className="neo-card px-4 py-2 text-center">
            <p className="text-2xl font-extrabold text-[#00E676]">{status.free_remaining}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Spins gratuits</p>
          </div>
          <div className="neo-card px-4 py-2 text-center">
            <p className="text-2xl font-extrabold text-[#FFD700]">{status.paid_remaining}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Spins achetés</p>
          </div>
        </div>
      )}

      {/* Wheel */}
      <div className="flex flex-col items-center gap-4">
        <WheelCanvas spinning={spinning} targetIndex={targetIndex} onSpinEnd={onSpinEnd} />
        <button
          onClick={handleSpin}
          disabled={spinning || (status && status.total_available <= 0)}
          className={`neo-button-primary px-10 py-3 text-lg font-bold uppercase tracking-wider flex items-center gap-2 ${
            spinning ? "opacity-70" : ""
          }`}
          data-testid="spin-btn"
        >
          {spinning ? (
            <><Loader2 size={22} className="animate-spin" /> En cours...</>
          ) : status && status.total_available <= 0 ? (
            "Plus de spins"
          ) : (
            <><Zap size={22} /> Tourner la roue</>
          )}
        </button>
        {status && status.total_available <= 0 && (
          <button
            onClick={() => setShowBuyModal(true)}
            className="neo-button-secondary px-8 py-2 text-sm font-bold flex items-center gap-2 mt-2"
            data-testid="no-spins-buy-btn"
          >
            <ShoppingCart size={18} /> Acheter des spins
          </button>
        )}
      </div>

      {/* Prize Result Modal */}
      {showResult && prize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="prize-modal">
          <div className="neo-card p-6 sm:p-8 max-w-sm w-[90%] text-center relative animate-in zoom-in-95 duration-300" style={{ borderColor: prize.color, boxShadow: `0 0 60px ${prize.color}40` }}>
            <button onClick={() => setShowResult(false)} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
              <X size={20} />
            </button>
            <div
              className="w-20 h-20 border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: prize.color, boxShadow: `0 0 30px ${prize.color}60, 4px 4px 0px #0A0A0A`, animation: "pulse 1.5s ease-in-out infinite" }}
            >
              <PrizeIcon size={40} className="text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold mb-2" style={{ textShadow: `0 0 20px ${prize.color}40` }}>VOUS AVEZ GAGNE !</h2>
            <p className="font-bold text-lg" style={{ color: prize.color }}>{prize.label}</p>
            <p className="text-sm text-muted-foreground mt-2">{prize.description}</p>
            {prize.delivery?.method === "email" && (
              <div className={`mt-3 p-3 flex items-center gap-2 text-sm ${prize.delivery?.email_sent ? "bg-[#00E676]/10 border border-[#00E676]" : "bg-[#FFD700]/10 border border-[#FFD700]"}`}>
                {prize.delivery?.email_sent ? <Mail size={16} className="text-[#00E676]" /> : <Gift size={16} className="text-[#FFD700]" />}
                <span>{prize.delivery?.email_sent ? "Envoyé à votre email !" : "Lot sauvegardé ! Retrouvez-le dans 'Mes Gains' ci-dessous."}</span>
              </div>
            )}
            {prize.delivery?.method === "auto_activated" && (
              <div className="mt-3 bg-[#FF5722]/10 border border-[#FF5722] p-3 flex items-center gap-2 text-sm">
                <Check size={16} className="text-[#FF5722]" />
                <span>Abonnement activé pour 1 mois !</span>
              </div>
            )}
            {prize.delivery?.method === "contact_support" && (
              <div className="mt-3 bg-[#FFD700]/10 border border-[#FFD700] p-3 flex items-center gap-2 text-sm">
                <Mail size={16} className="text-[#FFD700]" />
                <span>Envoyez un email au support pour recevoir votre lot</span>
              </div>
            )}
            <button
              onClick={() => { setShowResult(false); fetchMyPrizes(); }}
              className="neo-button-primary mt-4 w-full"
            >
              Continuer
            </button>
          </div>
        </div>
      )}

      {/* Buy Spins Modal */}
      {showBuyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="buy-spins-modal">
          <div className="neo-card p-6 sm:p-8 max-w-md w-[90%] text-center border-[#FFD700] shadow-[0_0_40px_rgba(255,215,0,0.2)] bg-background relative">
            <button onClick={() => setShowBuyModal(false)} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
              <X size={20} />
            </button>
            <div className="w-14 h-14 bg-[#FFD700] border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-4 shadow-[3px_3px_0px_#0A0A0A]">
              <Gift size={28} className="text-[#0A0A0A]" />
            </div>
            <h2 className="font-extrabold text-lg sm:text-xl uppercase tracking-tight mb-1">Acheter des spins</h2>
            <p className="text-muted-foreground text-sm mb-5">Continuez à jouer et gagnez des lots exclusifs</p>
            <div className="space-y-3">
              {Object.entries(status?.packs || {}).map(([id, pack]) => (
                <button
                  key={id}
                  onClick={() => { setShowBuyModal(false); handleBuyPack(id); }}
                  disabled={!!buyingPack}
                  className="w-full neo-card p-4 flex items-center justify-between hover:border-[#FFD700] transition-colors"
                  data-testid={`modal-buy-${id}`}
                >
                  <div className="text-left">
                    <p className="font-bold">{pack.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {pack.spins > 1 ? `${(pack.price / pack.spins).toFixed(2)}€/spin` : "Spin unique"}
                    </p>
                  </div>
                  <span className="text-xl font-extrabold text-[#FFD700]">{pack.price.toFixed(0)}€</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4">Paiement sécurisé via Stripe</p>
          </div>
        </div>
      )}

      {/* Prizes list */}
      <div className="neo-card p-4 sm:p-6" data-testid="prizes-list">
        <h2 className="font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Star className="text-[#FFD700]" size={18} />
          Lots disponibles
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(status?.prizes || []).map((p) => {
            const Icon = PRIZE_ICONS[p.id] || Gift;
            return (
              <div key={p.id} className="flex items-center gap-3 p-3 border-2 border-[#0A0A0A]/10 dark:border-white/10">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border-2 border-[#0A0A0A]" style={{ backgroundColor: p.color }}>
                  <Icon size={20} className="text-white" />
                </div>
                <span className="font-bold text-sm">{p.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Buy Spins */}
      <div className="neo-card p-4 sm:p-6" data-testid="buy-spins">
        <h2 className="font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <ShoppingCart className="text-[#00E676]" size={18} />
          Acheter des spins
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(status?.packs || {}).map(([id, pack]) => (
            <button
              key={id}
              onClick={() => handleBuyPack(id)}
              disabled={!!buyingPack}
              className="neo-card p-4 text-center hover:border-[#FFD700] transition-colors group"
              data-testid={`buy-pack-${id}`}
            >
              <p className="text-2xl font-extrabold">{pack.price.toFixed(0)}€</p>
              <p className="font-bold text-sm mt-1">{pack.label}</p>
              <p className="text-xs text-muted-foreground">
                {pack.spins > 1 ? `${(pack.price / pack.spins).toFixed(2)}€/spin` : ""}
              </p>
              {buyingPack === id && <Loader2 size={16} className="animate-spin mx-auto mt-2" />}
            </button>
          ))}
        </div>
      </div>

      {/* My Prizes (stored in DB) */}
      {myPrizes.length > 0 && (
        <div className="neo-card p-4 sm:p-6" data-testid="my-prizes">
          <h2 className="font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
            <Gift className="text-[#FFD700]" size={18} />
            Mes Gains ({myPrizes.length})
          </h2>
          <div className="space-y-3">
            {myPrizes.map((p, idx) => (
              <div key={idx} className="border-2 border-[#0A0A0A]/10 dark:border-white/10">
                <button
                  onClick={() => setExpandedPrize(expandedPrize === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-3 hover:bg-[#00E676]/5 transition-colors"
                  data-testid={`prize-expand-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center border-2 border-[#0A0A0A] ${p.type === "products" ? "bg-[#00E676]" : "bg-[#00BCD4]"}`}>
                      {p.type === "products" ? <Package size={16} className="text-[#0A0A0A]" /> : <Truck size={16} className="text-white" />}
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-sm">{p.label}</p>
                      <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString("fr-FR")} — {p.items?.length || 0} items</p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{expandedPrize === idx ? "Masquer" : "Voir"}</span>
                </button>
                {expandedPrize === idx && p.items && (
                  <div className="border-t border-[#0A0A0A]/10 dark:border-white/10 p-3 space-y-2">
                    {p.items.map((item, j) => (
                      <div key={j} className="flex items-center justify-between py-1 text-sm">
                        <span className="truncate flex-1 mr-2">{item.name}</span>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {item.price && <span className="font-bold text-[#00E676]">{item.price}€</span>}
                          <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[#00BCD4] underline text-xs">Voir</a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Wins */}
      {status?.recent_wins?.length > 0 && (
        <div className="neo-card p-4 sm:p-6" data-testid="recent-wins">
          <h2 className="font-bold text-sm uppercase tracking-wider mb-3">Derniers gains</h2>
          <div className="space-y-2">
            {status.recent_wins.slice(0, 5).map((win, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-[#0A0A0A]/10 dark:border-white/10 text-sm">
                <span className="font-bold">{win.prize_label}</span>
                <span className="text-xs text-muted-foreground">{new Date(win.timestamp).toLocaleDateString("fr-FR")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
